/**
 * Students API Service
 * Handles all student-related API calls
 */

import { apiGet, apiPost, apiPut, apiDelete } from '../manager';
import { API_ENDPOINTS } from '../config';

/**
 * Get all students
 * @param {Object} params - Query parameters (page, limit, search, etc.)
 * @param {Object} options - Additional options (headers, useMock, etc.)
 * @returns {Promise<Object>} Students list
 */
export const getStudents = async (params = {}) => {
  const queryParams = {
    page: params.page || 1,
    page_size: params.page_size || 10,
  };
  if (params.branch && params.branch !== 'All Branches') queryParams.branch = params.branch;
  if (params.year && params.year !== 'All Years') queryParams.year = params.year;
  if (params.semester && params.semester !== 'All Semesters') queryParams.semester = params.semester;
  return apiGet(API_ENDPOINTS.STUDENTS.LIST, { params: queryParams });
};

/**
 * Get student by ID
 * @param {string} id - Student ID
 * @param {Object} options - Additional options (headers, useMock, etc.)
 * @returns {Promise<Object>} Student details
 */

export const getStudentById = async (id) => {
  return apiGet(API_ENDPOINTS.STUDENTS.DETAIL(id));
};

/**
 * Create a new student
 * @param {Object} studentData - Student data
 * @param {Object} options - Additional options (headers, useMock, etc.)
 * @returns {Promise<Object>} Created student
 */
export const createStudent = async (studentData) => {
  return apiPost(API_ENDPOINTS.STUDENTS.CREATE, studentData);
};


/**
 * Update student
 * @param {string} id - Student ID
 * @param {Object} studentData - Updated student data
 * @param {Object} options - Additional options (headers, useMock, etc.)
 * @returns {Promise<Object>} Updated student
 */
export const updateStudent = async (id, studentData) => {
  return apiPut(API_ENDPOINTS.STUDENTS.UPDATE(id), studentData);
};


/**
 * Delete student
 * @param {string} id - Student ID
 * @param {Object} options - Additional options (headers, useMock, etc.)
 * @returns {Promise<Object>} Deletion result
 */
export const deleteStudent = async (id) => {
  return apiDelete(API_ENDPOINTS.STUDENTS.DELETE(id));
};

/**
 * Search students
 * @param {string} query - Search query
 * @param {Object} options - Additional options (headers, useMock, etc.)
 * @returns {Promise<Object>} Search results
 */
export const searchStudentById = async (student_id) => {
  const response = await fetch('https://prod-api.botguru.ai/api/v1/accounts/university/students/search/', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ student_id }),
  });
  const data = await response.json();
  return { success: response.ok, data };
};
export const searchStudentResultsById = async (student_id) => {
  const response = await fetch('https://prod-api.botguru.ai/api/v1/accounts/university/students/evaluations/search/', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ student_id }),
  });
  const data = await response.json();
  return { success: response.ok, data };
};



export const getStudentResults = async (filters = {}) => {
  const PARAM_MAP = {
    branch:        { param: "branch",               ignore: "All Branches" },
    year:          { param: "year",                 ignore: "All Years" },
    semester:      { param: "semester",             ignore: "All Semesters" },
    language:      { param: "language",             ignore: "All Languages" },
    database:      { param: "database",             ignore: "All Databases" },
    programming:   { param: "programming_level",    ignore: "All" },
    dbLevel:       { param: "database_level",       ignore: "All" },
    communication: { param: "soft_skills_level",    ignore: "All" },
    aptitude:      { param: "aptitude_level",       ignore: "All" },
    status:        { param: "status",               ignore: "All Status" },
    overallStatus: { param: "overall_status",       ignore: "All" },
    scoreMin:      { param: "min_overall_score", ignore: ["", "0"] },
    scoreMax:      { param: "max_overall_score", ignore: ["", "100"] },
  };

  const queryParams = {
    page: filters.page || 1,
    page_size: filters.page_size || 100,
  };

  Object.entries(PARAM_MAP).forEach(([key, { param, ignore }]) => {
    const val = filters[key];
    const ignored = Array.isArray(ignore) ? ignore.includes(String(val)) : String(val) === String(ignore);
    if (val !== undefined && val !== null && !ignored) {
      queryParams[param] = val;
    }
  });

  return apiGet(API_ENDPOINTS.STUDENTS.RESULTS, { params: queryParams });
};

/**
 * Bulk upload students from CSV
 * @param {Array} students - Array of student objects from CSV
 * @param {Object} options - Additional options (headers, useMock, etc.)
 * @returns {Promise<Object>} Upload result
 */
export const bulkUploadStudents = async (students) => {
  const response = await fetch('https://prod-api.botguru.ai/api/v1/accounts/university/students/upload', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ students }),
  });
  const data = await response.json();
  return {
    success: response.ok,
    data: {
      created: data.created ?? data.data?.created ?? 0,
      updated: data.updated ?? data.data?.updated ?? 0,
      errors: data.errors ?? data.data?.errors ?? [],
      message: data.message || (response.ok ? 'Upload successful' : 'Upload failed'),
    },
  };
};
